import { Button, Column, Divider, Icon } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import arrowRightTokens from "@amzn/meridian-tokens/base/icon/arrow-right-large";
import { useState } from "react";
import BotMessage from "src/components/shared/botMessage/botMessage";
import DenyConfirmationMessage from "src/components/shared/denyConfirmationMessage/denyConfirmationMessage";
import UserMessage from "src/components/shared/userMessage/userMessage";
import { ConfirmationTurn, ExceptionTurn, TextTurn, Turn } from "../../types";
import StateTree from "./stateTree/stateTree";
import "./chatMessages.scss";
import ChartLoader from "./chartLoader";
import ChartBlock from "./chartBlock";
import starFilledTokens from "@amzn/meridian-tokens/base/icon/star-filled";
import ChatExceptionView from "../chatExceptionView";
import UserFeedback from "./userFeedback/userFeedback";
import SourcesDropdown from "src/components/shared/sourcesDropDown/sourcesDropdown";
import CitationText from "src/components/shared/citationText/citationText";
import { processCitations } from "src/utils/citationUtils"; // Add this import

interface ChatMessagesProps {
  userAlias: string;
  turns: Turn[];
}
const ChatMessages = ({ turns, userAlias }: ChatMessagesProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(null);
  const [selectedTurn, setSelectedTurn] = useState<TextTurn>(null);

  const isChatExceptionTurn = (turn: Turn): turn is ExceptionTurn => {
    return turn.type === "exception";
  };

  const isTextTurn = (turn: Turn): turn is TextTurn => {
    return turn.type === "text";
  };

  const isConfirmationTurn = (turn: Turn): turn is ConfirmationTurn => {
    return turn.type === "confirmation";
  };

  // there's 2 messages per turn - we want the index of the bot message, not the user message
  const findMessageIndexInTurn = (index: number) => index * 2 + 1;

  const toggleRow = (turn: TextTurn, index: number) => {
    // there's 2 messages per turn - we want the index of the bot message, not the user message
    const mappedIndex = findMessageIndexInTurn(index);
    if (selectedIndex !== mappedIndex) {
      setSelectedTurn(turn);
      setSelectedIndex(index * 2 + 1);
    } else {
      setSelectedTurn(null);
      setSelectedIndex(null);
    }
  };

  return (
    <div className="chat-messages">
      {turns.map((turn, index) => {
        const isSelected = selectedIndex === findMessageIndexInTurn(index);
        return (
          <>
            {isChatExceptionTurn(turn) && (
              <ChatExceptionView turn={turn} userAlias={userAlias} />
            )}

            {isTextTurn(turn) && (
              <>
                <div className="chat-message__row">
                  <UserMessage
                    maxWidth="525px"
                    alias={userAlias}
                    className="chat-messages__message"
                  >
                    {turn.userRequest}
                  </UserMessage>
                  {turn.isFavorite && (
                    <span className="chat-message__favorite-icon">
                      <Icon tokens={starFilledTokens} />
                    </span>
                  )}
                </div>
                <Column
                  className="chat-messages__message"
                  spacingInset={"400"}
                  type={isSelected ? "fill" : null}
                  alignmentHorizontal="end"
                >
                  <BotMessage maxWidth="525px">
                    <CitationText
                      text={processCitations(
                        turn.systemResponse,
                        turn.sourceAttributions,
                      )}
                      citations={turn.sourceAttributions}
                    />
                    {turn.relatedDocuments && (
                      <>
                        <Divider spacingBefore="400" spacingAfter="400" />
                        <SourcesDropdown
                          relatedDocuments={turn.relatedDocuments}
                          sourceAttributions={turn.sourceAttributions}
                        />
                      </>
                    )}
                    {turn?.voteUp !== null && (
                      <UserFeedback
                        voteUp={turn.voteUp}
                        downvoteReason={turn.downvoteReason}
                      />
                    )}
                  </BotMessage>
                  <Button
                    className="chat-messages__toggle-details-btn"
                    type="tertiary"
                    size="small"
                    onClick={() => toggleRow(turn, index)}
                  >
                    {`${isSelected ? "Hide" : "Show"} Details`}
                    <Icon
                      tokens={isSelected ? arrowLeftTokens : arrowRightTokens}
                    />
                  </Button>
                </Column>
              </>
            )}
            {isConfirmationTurn(turn) &&
              (turn.confirmedYes ? (
                <>
                  <UserMessage
                    alias={userAlias}
                    className="chat-messages__message"
                  >
                    Yes
                  </UserMessage>
                  {turn.emptyData ? (
                    <ChartBlock turn={turn} />
                  ) : (
                    <ChartLoader turn={turn} />
                  )}
                </>
              ) : (
                <>
                  <UserMessage
                    alias={userAlias}
                    className="chat-messages__message"
                  >
                    No
                  </UserMessage>
                  <DenyConfirmationMessage className="chat-messages__message" />
                </>
              ))}
          </>
        );
      })}

      {selectedIndex !== null && (
        <div
          className="chat-messages__details-container"
          style={{ gridRowStart: selectedIndex + 1 }}
        >
          <div className="chat-messages__details">
            <StateTree selectedTurn={selectedTurn} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessages;
